import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/sections/Nav";
import Footer from "@/sections/Footer";
import { Chapter } from "@/components/Chapter";
import { Reveal } from "@/components/Reveal";
import { API_URL } from "@/config/site";
import { Mail } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ full_name: "", work_email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Contact — WSafe AI by WorkSafeAgent";
    const topic = new URLSearchParams(window.location.search).get("topic");
    if (topic === "pilot") {
      setForm((f) => (f.message ? f : { ...f, message: "I'd like to book a paid pilot." }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/demo-requests`, {
        full_name: form.full_name,
        work_email: form.work_email,
        company: form.company,
        role: "Contact page",
        sites: "—",
        tier_interest: null,
        message: form.message || null,
      });
      toast.success("Message sent", { description: "A person will reply — usually within one business day." });
      setForm({ full_name: "", work_email: "", company: "", message: "" });
    } catch {
      toast.error("Something went wrong — please email info@wsafeai.com directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-line rounded-lg px-4 py-3 text-sm text-ink focus:border-safety focus:outline-none transition-colors";
  const labelCls = "text-[11px] font-bold uppercase tracking-[0.2em] text-steel block mb-2";

  return (
    <div className="bg-white text-ink font-sans antialiased min-h-screen">
      <Nav />
      <main className="pt-36 pb-24">
        <div className="mx-auto max-w-[1160px] px-6">
          <Chapter
            eyebrow="Contact"
            title="Talk to a person."
            lede="Request a paid pilot, book a compliance evaluation, or ask anything — a person reads and replies to every message."
          />
          <div className="grid lg:grid-cols-5 gap-10 max-w-5xl">
            <Reveal className="lg:col-span-3">
              <form data-testid="contact-form" onSubmit={submit} className="space-y-5">
                <div>
                  <label className={labelCls}>Full name</label>
                  <input data-testid="contact-input-name" required minLength={2} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Work email</label>
                  <input data-testid="contact-input-email" type="email" required value={form.work_email} onChange={(e) => setForm({ ...form, work_email: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Company</label>
                  <input data-testid="contact-input-company" required minLength={2} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Message</label>
                  <textarea data-testid="contact-input-message" rows={5} maxLength={2000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
                </div>
                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={loading}
                  className="rounded-lg bg-safety text-white font-bold px-8 py-4 hover:bg-[#d97c12] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send message"}
                </button>
              </form>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-2">
              <div className="bg-wash border border-line rounded-[14px] p-8 h-full">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel mb-6">Prefer email?</p>
                <a
                  data-testid="contact-email-link"
                  href="mailto:info@wsafeai.com"
                  className="inline-flex items-center gap-3 text-lg font-bold text-safety hover:text-[#d97c12] transition-colors"
                >
                  <Mail size={18} /> info@wsafeai.com
                </a>
                <p className="mt-8 text-sm text-steel leading-relaxed">
                  WorkSafeAgent LLC · Abilene, Texas · patent pending
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
